import DateInput from '../components/DateInput';
// ============================================================
// ALL REMAINING ADMIN PAGES
// Patients · Rota · Pending Slots · Billing · Payouts · Analytics · Services
// ============================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, eachDayOfInterval,
         startOfWeek, endOfWeek, isSameMonth, isToday, addMonths, subMonths } from 'date-fns';
import { el } from 'date-fns/locale';
import { patientAPI, rotaAPI, billingAPI, payoutAPI, analyticsAPI, serviceAPI, doctorAPI } from '../services/api';
import { PageHeader, DataTable, AvatarCell, ActionBtn, Modal,
         FormField, Input, Select, Card, BarChart, StatCard } from '../components/Shared';
import toast from 'react-hot-toast';
import styles from './Pages.module.css';

const AVATAR_COLORS = ['#C0185A','#1E3A5F','#0D9A6A','#E07B2A','#6B46C1','#2B6CB0'];
const ac = (i) => AVATAR_COLORS[i % AVATAR_COLORS.length];

// Format any value (number or numeric string from Postgres NUMERIC) as €X.XX.
// Defensive: handles null/undefined/empty string by returning €0.00.
const fmtEuro = (v) => `€${Number(v || 0).toFixed(2)}`;

// ════════════════════════════════
// PATIENTS
// ════════════════════════════════
export function PatientsPage() {
  const [patients, setPatients] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    patientAPI.getAll()
      .then(r => setPatients(r.data.data))
      .catch(() => toast.error('Αδυναμία φόρτωσης ασθενών'))
      .finally(() => setLoading(false));
  }, []);

  const COLS = [
    { key:'name',    label:'Ασθενής',       width:'2fr',   render:(r,i)=><AvatarCell initials={`${r.firstName?.[0]}${r.lastName?.[0]}`} name={`${r.firstName} ${r.lastName}`} sub={r.email} color={ac(i)} /> },
    { key:'amka',    label:'ΑΜΚΑ',          width:'1.2fr', render:r=>r.amka },
    { key:'joined',  label:'Εγγραφή',       width:'1fr',   render:r=>r.createdAt ? new Date(r.createdAt).toLocaleDateString('el-GR') : '—' },
    { key:'count',   label:'Διαβουλεύσεις', width:'1fr',   render:r=><strong>{r.totalConsultations||0}</strong> },
    { key:'plan',    label:'Πακέτο',        width:'1fr',   render:()=><span className="pill pill-navy">Pay-per-use</span> },
    { key:'actions', label:'',              width:'90px',  render:r=><ActionBtn onClick={()=>navigate(`/patients/${r.id}`)}>Προβολή</ActionBtn> },
  ];

  return (
    <div className={styles.page}>
      <PageHeader title="Όλοι οι Ασθενείς" subtitle={`${patients.length} εγγεγραμμένοι ασθενείς`}
        action={<ActionBtn variant="default" onClick={()=>toast('Εξαγωγή...')}>⬇ Εξαγωγή CSV</ActionBtn>} />
      {loading ? <div className={styles.loading}>Φόρτωση...</div> : <DataTable columns={COLS} rows={patients} />}
    </div>
  );
}

// ════════════════════════════════
// ROTA CALENDAR
// ════════════════════════════════
export function RotaPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [slots, setSlots]             = useState({});
  const [loading, setLoading]         = useState(true);
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [addStep, setAddStep]         = useState(1);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [slotForm, setSlotForm] = useState({ date:'', startTime:'10:00', endTime:'14:00', serviceType:'Digital GP', duration:15, maxPatients:16, sessionType:'non_contractual', repeat:'none' });

  const DAY_NAMES = ['ΔΕΥ','ΤΡΙ','ΤΕΤ','ΠΕΜ','ΠΑΡ','ΣΑΒ','ΚΥΡ'];

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await rotaAPI.getCalendar(currentDate.getFullYear(), currentDate.getMonth() + 1);
      const map = {};
      data.data.forEach(s => {
        if (!map[s.slotDate]) map[s.slotDate] = [];
        map[s.slotDate].push(s);
      });
      setSlots(map);
    } catch { toast.error('Αδυναμία φόρτωσης ρόστερ'); }
    finally { setLoading(false); }
  };

  // @m24-deps: dependency list left exactly as it was when linting was switched on 25 Sep 2026.
  // Adding the missing dependency can change how often this effect runs, so it is a behaviour
  // change to make deliberately and test, not a lint tidy-up. Recorded, not silenced by turning
  // the rule off - it still guards every effect written from here on.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [currentDate]);

  const monthStart = startOfMonth(currentDate);
  const monthEnd   = endOfMonth(currentDate);
  const calStart   = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calEnd     = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const calDays    = eachDayOfInterval({ start: calStart, end: calEnd });

  const handleAddSlot = async () => {
    try {
      await rotaAPI.addSlot({ ...slotForm, doctorId: selectedDoctor?.id });
      toast.success('✅ Το slot προστέθηκε και δημοσιεύτηκε στους ασθενείς');
      setShowAddSlot(false);
      setAddStep(1);
      setSelectedDoctor(null);
      load();
    } catch { toast.error('Σφάλμα προσθήκης slot'); }
  };

  const [doctors, setDoctors] = React.useState([]);
  React.useEffect(() => {
    doctorAPI.getAll().then(r => {
      setDoctors(r.data.data.map((d, i) => ({
        id: d.id,
        name: `Δρ. ${d.first_name} ${d.last_name}`,
        spec: d.specialty || 'Γενικός Ιατρός',
        initials: `${d.first_name?.[0]}${d.last_name?.[0]}`,
        color: ["#C0185A","#0D9A6A","#1E3A5F","#E07B2A"][i % 4]
      })));
    }).catch(() => { /* @m24-silence: pre-existing swallow, kept as-is when linting was switched on 25 Sep 2026; why it is safe has not been established */ });
  }, []);
  const mockDoctors = doctors;

  return (
    <div className={styles.page}>
      <PageHeader title={`Ρόστερ — ${format(currentDate,'MMMM yyyy',{locale:el})}`}
        subtitle="Διαχείριση διαθεσιμότητας ιατρών"
        action={
          <div style={{display:'flex',gap:8}}>
            <ActionBtn onClick={()=>setCurrentDate(d=>subMonths(d,1))}>‹ Προηγ.</ActionBtn>
            <ActionBtn onClick={()=>setCurrentDate(new Date())}>Σήμερα</ActionBtn>
            <ActionBtn onClick={()=>setCurrentDate(d=>addMonths(d,1))}>Επόμ. ›</ActionBtn>
            <ActionBtn variant="primary" onClick={()=>{setShowAddSlot(true);setAddStep(1);}}>+ Προσθήκη στο Ρόστερ</ActionBtn>
          </div>
        }
      />

      <div className={styles.rotaLegend}>
        <span><span className={styles.legendNc}/> Μη Συμβατική</span>
        <span><span className={styles.legendC}/> Συμβατική</span>
        <span><span className={styles.legendPending}/> Εκκρεμεί</span>
      </div>

      {loading ? <div className={styles.loading}>Φόρτωση...</div> : (
      <div className={styles.rotaCalendar}>
        <div className={styles.rotaHead}>
          {DAY_NAMES.map(d=><div key={d} className={styles.rotaHeadDay}>{d}</div>)}
        </div>
        <div className={styles.rotaBody}>
          {calDays.map(day => {
            const key = format(day,'yyyy-MM-dd');
            const daySlots = slots[key] || [];
            const inMonth = isSameMonth(day, currentDate);
            const today = isToday(day);
            const wd = day.getDay();
            const weekend = wd===0||wd===6;
            return (
              <div key={key} className={[styles.rotaCell, !inMonth&&styles.otherMonth, today&&styles.today, weekend&&styles.weekend].filter(Boolean).join(' ')}>
                <div className={styles.rotaCellDate}>{today ? <span className={styles.todayBadge}>{format(day,'d')}</span> : format(day,'d')}</div>
                {inMonth && daySlots.map((s,i)=>(
                  <div key={i} className={[styles.rotaSlot, s.status==='pending'?styles.slotPending : s.sessionType==='contractual'?styles.slotC:styles.slotNc].join(' ')}>
                    <span>{s.startTime?.slice(0,5)}</span>
                    <span>{s.doctorLastName}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Add to Rota modal — 3 steps */}
      <Modal open={showAddSlot} onClose={()=>{setShowAddSlot(false);setAddStep(1);setSelectedDoctor(null);}} title="Προσθήκη στο Ρόστερ" size="md">
        {/* Step tabs */}
        <div className={styles.stepTabs}>
          {['1 · Επιλογή Ιατρού','2 · Ορισμός Ωραρίου','3 · Επιβεβαίωση'].map((s,i)=>(
            <div key={i} className={`${styles.stepTab} ${addStep===i+1?styles.stepTabActive:addStep>i+1?styles.stepTabDone:''}`}>{s}</div>
          ))}
        </div>

        {/* Step 1 */}
        {addStep===1 && (
          <div style={{padding:20}}>
            <p style={{fontSize:13,color:'var(--muted)',marginBottom:16}}>Επιλέξτε τον ιατρό για τον οποίο θέλετε να προσθέσετε διαθεσιμότητα:</p>
            {mockDoctors.map(d=>(
              <div key={d.id}
                className={`${styles.doctorOption} ${selectedDoctor?.id===d.id?styles.docSelected:''}`}
                onClick={()=>setSelectedDoctor(d)}
              >
                <div className={styles.docAv} style={{background:d.color}}>{d.initials}</div>
                <div style={{flex:1}}><div style={{fontSize:14,fontWeight:700,color:'var(--navy)'}}>{d.name}</div><div style={{fontSize:12,color:'var(--muted)'}}>{d.spec}</div></div>
                {selectedDoctor?.id===d.id && <span style={{color:'var(--crimson)',fontSize:20}}>✓</span>}
              </div>
            ))}
            <div className={styles.modalFooter}>
              <ActionBtn variant="default" onClick={()=>{setShowAddSlot(false);setSelectedDoctor(null);}}>Ακύρωση</ActionBtn>
              <ActionBtn variant="primary" onClick={()=>setAddStep(2)} disabled={!selectedDoctor}>Επόμενο →</ActionBtn>
            </div>
          </div>
        )}

        {/* Step 2 */}
        {addStep===2 && (
          <div>
            <div style={{padding:'12px 20px',background:'var(--bg)',borderBottom:'1px solid var(--border)',display:'flex',gap:10,alignItems:'center'}}>
              <span style={{fontSize:13,color:'var(--muted)'}}>Ιατρός:</span>
              <strong style={{fontSize:14,color:'var(--navy)'}}>{selectedDoctor?.name}</strong>
            </div>
            <div className={styles.slotFormGrid}>
              <FormField label="Ημερομηνία" required><DateInput value={slotForm.date} onChange={e=>setSlotForm({...slotForm,date:e.target.value})} /></FormField>
              <FormField label="Τύπος Υπηρεσίας" required><Select value={slotForm.serviceType} onChange={e=>setSlotForm({...slotForm,serviceType:e.target.value})}><option>Digital GP</option></Select></FormField>
              <FormField label="Ώρα Έναρξης" required><Input type="time" value={slotForm.startTime} onChange={e=>setSlotForm({...slotForm,startTime:e.target.value})} /></FormField>
              <FormField label="Ώρα Λήξης" required><Input type="time" value={slotForm.endTime} onChange={e=>setSlotForm({...slotForm,endTime:e.target.value})} /></FormField>
              <FormField label="Διάρκεια Slot"><Select value={slotForm.duration} onChange={e=>setSlotForm({...slotForm,duration:+e.target.value})}><option value={15}>15 λεπτά</option><option value={20}>20 λεπτά</option><option value={30}>30 λεπτά</option></Select></FormField>
              <FormField label="Μέγιστοι Ασθενείς"><Input type="number" value={slotForm.maxPatients} min={1} max={50} onChange={e=>setSlotForm({...slotForm,maxPatients:+e.target.value})} /></FormField>
              <FormField label="Τύπος Συνεδρίας"><Select value={slotForm.sessionType} onChange={e=>setSlotForm({...slotForm,sessionType:e.target.value})}><option value="non_contractual">Μη Συμβατική</option><option value="contractual">Συμβατική</option></Select></FormField>
              <FormField label="Επανάληψη"><Select value={slotForm.repeat} onChange={e=>setSlotForm({...slotForm,repeat:e.target.value})}><option value="none">Χωρίς επανάληψη</option><option value="weekly">Εβδομαδιαία</option></Select></FormField>
            </div>
            {/* Preview */}
            {slotForm.startTime && slotForm.endTime && (
              <div className={styles.slotPreview}>
                <strong>Προεπισκόπηση:</strong> {Math.floor(((+slotForm.endTime.split(':')[0]*60+ +slotForm.endTime.split(':')[1])-(+slotForm.startTime.split(':')[0]*60+ +slotForm.startTime.split(':')[1]))/slotForm.duration)} slots ασθενών · {slotForm.startTime}–{slotForm.endTime} · {slotForm.duration} λεπτά έκαστο
              </div>
            )}
            <div className={styles.modalFooter}>
              <ActionBtn variant="default" onClick={()=>setAddStep(1)}>← Πίσω</ActionBtn>
              <ActionBtn variant="primary" onClick={()=>setAddStep(3)} disabled={!slotForm.date}>Επόμενο →</ActionBtn>
            </div>
          </div>
        )}

        {/* Step 3 */}
        {addStep===3 && (
          <div style={{padding:20}}>
            <div className={styles.confirmCard}>
              <div style={{fontSize:28,marginBottom:14,textAlign:'center'}}>📅</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px 20px'}}>
                {[['Ιατρός',selectedDoctor?.name],['Ημερομηνία',slotForm.date],['Ώρα',`${slotForm.startTime} – ${slotForm.endTime}`],['Υπηρεσία',slotForm.serviceType],['Μέγιστοι ασθενείς',slotForm.maxPatients],['Τύπος',slotForm.sessionType==='contractual'?'Συμβατική':'Μη Συμβατική']].map(([k,v])=>(
                  <div key={k}><div style={{fontSize:11,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:3}}>{k}</div><div style={{fontSize:14,fontWeight:600,color:'var(--navy)'}}>{v}</div></div>
                ))}
              </div>
            </div>
            <div className={styles.confirmNote}>✅ Το slot θα δημοσιευτεί αμέσως και θα είναι ορατό στους ασθενείς.</div>
            <div className={styles.modalFooter}>
              <ActionBtn variant="default" onClick={()=>setAddStep(2)}>← Πίσω</ActionBtn>
              <ActionBtn variant="approve" onClick={handleAddSlot}>✓ Προσθήκη στο Ρόστερ</ActionBtn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ════════════════════════════════
// BILLING
// ════════════════════════════════
// @billing-status-expired-failed-v1 — must match CHECKOUT_HOLD_MINUTES in the
// backend (constants/booking-windows.js). Exported for tests.
export const CHECKOUT_HOLD_MS = 5 * 60 * 1000;
export function billingStatus(r, now = Date.now()) {
  if (r.status === 'completed') return { label: 'Πληρώθηκε', cls: 'pill-green' };
  if (r.status === 'refunded')  return { label: 'Επιστροφή', cls: 'pill-red' };
  if (r.status === 'failed')    return { label: 'Απέτυχε',   cls: 'pill-crimson' };
  const created = r.createdAt ? new Date(r.createdAt).getTime() : NaN;
  if (Number.isFinite(created) && now - created > CHECKOUT_HOLD_MS) {
    return { label: 'Έληξε', cls: 'pill-navy' };
  }
  return { label: 'Εκκρεμεί', cls: 'pill-orange' };
}

export function BillingPage() {
  const [txns,    setTxns]    = useState([]);
  const [bStats,  setBStats]  = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([billingAPI.getTransactions(), billingAPI.getStats()])
      .then(([t,s])=>{ setTxns(t.data.data); setBStats(s.data.data); })
      .catch(()=>toast.error('Αδυναμία φόρτωσης'))
      .finally(()=>setLoading(false));
  }, []);

  const COLS = [
    { key:'patient',  label:'Ασθενής',   width:'2fr',   render:(r,i)=><AvatarCell initials={`${r.patientFirstName?.[0]}${r.patientLastName?.[0]}`} name={`${r.patientFirstName} ${r.patientLastName}`} color={ac(i)} /> },
    { key:'amount',   label:'Ποσό',      width:'0.8fr', render:r=><strong style={{color:r.type==='refund'?'var(--danger)':undefined}}>{r.type==='refund'?'-':'+'}{fmtEuro(r.amountEuros)}</strong> },
    // @billing-paymentmethod-fix  Was comparing string field to number 2 — every real payment displayed as '🤖 Google Pay'.
    // Backend currently writes only 'card' (stripe.service.js:262); apple_pay/google_pay reserved for when backend captures them.
    { key:'method',   label:'Τρόπος',    width:'1fr',   render:r=>{
      const labels = { card:'💳 Κάρτα', apple_pay:'🍎 Apple Pay', google_pay:'🤖 Google Pay' };
      return labels[r.paymentMethod] || r.paymentMethod || '—';
    } },
    { key:'service',  label:'Υπηρεσία', width:'1fr',   render:r=>r.serviceType||'Digital GP' },
    { key:'date',     label:'Ημερομηνία',width:'1fr',  render:r=>r.createdAt ? new Date(r.createdAt).toLocaleDateString('el-GR') : '—' },
    // @billing-status-expired-failed-v1 (20 Oct 2026)
    // Every checkout attempt creates a 'pending' row; Viva keeps the checkout
    // page open for CHECKOUT_HOLD_MINUTES (5). A pending row older than that is
    // an abandoned checkout, not a payment in progress — shown as Έληξε so the
    // list stops reading as a wall of Εκκρεμεί. 'failed' (Viva 1798, a declined
    // card) is shown as Απέτυχε. Only a row younger than 5 min is Εκκρεμεί.
    { key:'status',   label:'Κατάσταση', width:'0.8fr', render:r=>{
      const s = billingStatus(r);
      return <span className={`pill ${s.cls}`}>{s.label}</span>;
    } },
  ];

  return (
    <div className={styles.page}>
      <PageHeader title="Χρεώσεις & Πληρωμές" subtitle="Όλες οι συναλλαγές"
        action={<ActionBtn onClick={()=>toast('Εξαγωγή...')}>⬇ Εξαγωγή</ActionBtn>} />
      <div className={styles.statsGrid4}>
        {/* @billing-status-expired-failed-v1: tiles count only completed payments
            (backend /billing/stats). Revenue is this month; the other three are
            all-time, so say so. The old "↑ 8%" was a hardcoded string, removed. */}
        <StatCard icon="💳" iconBg="rgba(13,154,106,0.1)" value={`€${Number(bStats.monthRevenue || 0).toLocaleString()}`} label="Έσοδα μήνα" />
        <StatCard icon="📋" iconBg="rgba(15,31,61,0.07)"  value={bStats.totalTransactions||0} label="Πληρωμές (σύνολο)" />
        <StatCard icon="↩"  iconBg="rgba(224,123,42,0.1)" value={`€${Number(bStats.refunds || 0).toLocaleString()}`} label="Επιστροφές (σύνολο)" trendDir="down" trend={`${bStats.refundCount||0} επιστροφές`} />
        <StatCard icon="💶" iconBg="rgba(192,24,90,0.1)"  value={fmtEuro(bStats.avgTransaction)} label="Μέση πληρωμή (σύνολο)" />
      </div>
      {loading ? <div className={styles.loading}>Φόρτωση...</div> : <DataTable columns={COLS} rows={txns} />}
    </div>
  );
}

// ════════════════════════════════
// PAYOUTS
// ════════════════════════════════
export function PayoutsPage() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth()===0?12:now.getMonth());
  const [year,  setYear]  = useState(now.getMonth()===0?now.getFullYear()-1:now.getFullYear());
  const [payouts, setPayouts] = useState([]);
  const [loading,setLoading]  = useState(true);

  const load = async () => {
    setLoading(true);
    try { const {data}=await payoutAPI.getPayouts(month,year); setPayouts(data.data); }
    catch { toast.error('Αδυναμία φόρτωσης'); }
    finally { setLoading(false); }
  };

  // @m24-deps: dependency list left exactly as it was when linting was switched on 25 Sep 2026.
  // Adding the missing dependency can change how often this effect runs, so it is a behaviour
  // change to make deliberately and test, not a lint tidy-up. Recorded, not silenced by turning
  // the rule off - it still guards every effect written from here on.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{load();},[month,year]);

  const handleApproveAll = async () => {
    try { await payoutAPI.approveAll(month,year); toast.success('💰 Όλες οι αμοιβές εγκρίθηκαν'); load(); }
    catch { toast.error('Σφάλμα'); }
  };

  const handleApproveSingle = async (doctorId) => {
    try { await payoutAPI.approveSingle(doctorId,month,year); toast.success('✅ Αμοιβή εγκρίθηκε'); load(); }
    catch { toast.error('Σφάλμα'); }
  };

  const MONTH_NAMES = ['','Ιανουάριος','Φεβρουάριος','Μάρτιος','Απρίλιος','Μάιος','Ιούνιος','Ιούλιος','Αύγουστος','Σεπτέμβριος','Οκτώβριος','Νοέμβριος','Δεκέμβριος'];

  // Sum net payouts. Postgres NUMERIC values arrive as strings, so we coerce
  // each one with Number() before summing — otherwise "12.50" + "12.50" = "12.5012.50".
  const total = payouts.reduce((s, p) => s + Number(p.netAmountEuros || 0), 0);

  const COLS = [
    { key:'doctor',    label:'Ιατρός',        width:'2fr',   render:(r,i)=><AvatarCell initials={`${r.firstName?.[0]}${r.lastName?.[0]}`} name={`Δρ. ${r.firstName} ${r.lastName}`} color={ac(i)} /> },
    { key:'consults',  label:'Διαβουλεύσεις', width:'1fr',   render:r=><strong>{r.totalConsultations}</strong> },
    { key:'gross',     label:'Ακαθάριστα',    width:'1fr',   render:r=>fmtEuro(r.grossAmountEuros) },
    { key:'fee',       label:'Προμήθεια',     width:'1fr',   render:r=><span style={{color:'var(--crimson)'}}>-{fmtEuro(r.platformFeeEuros)}</span> },
    { key:'net',       label:'Καθαρή Αμοιβή', width:'1fr',  render:r=><strong style={{color:'var(--success)'}}>{fmtEuro(r.netAmountEuros)}</strong> },
    { key:'actions',   label:'',             width:'120px',  render:r=>(
      r.status==='paid'
        ? <span className="pill pill-green">✓ Πληρώθηκε</span>
        : <ActionBtn variant="publish" onClick={()=>handleApproveSingle(r.doctorId)}>Πληρωμή</ActionBtn>
    )},
  ];

  return (
    <div className={styles.page}>
      <PageHeader title={`Αμοιβές Ιατρών — ${MONTH_NAMES[month]} ${year}`}
        subtitle="Διαβουλεύσεις που ολοκληρώθηκαν — αναμένουν έγκριση πριν την αποστολή"
        action={
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <select className={styles.monthSelect} value={month} onChange={e=>setMonth(+e.target.value)}>
              {MONTH_NAMES.slice(1).map((m,i)=><option key={i+1} value={i+1}>{m}</option>)}
            </select>
            <input className={styles.yearInput} type="number" value={year} onChange={e=>setYear(+e.target.value)} />
            {payouts.some(p=>p.status==='pending') && (
              <ActionBtn variant="approve" onClick={handleApproveAll}>💰 Έγκριση Όλων (€{total.toFixed(0)})</ActionBtn>
            )}
          </div>
        }
      />
      {loading ? <div className={styles.loading}>Φόρτωση...</div> : (
        payouts.length===0 ? (
          <div className={styles.allGood}><div style={{fontSize:48,marginBottom:14}}>📊</div><div style={{fontSize:16,fontWeight:700}}>Δεν υπάρχουν αμοιβές για αυτήν την περίοδο</div></div>
        ) : (
          <DataTable columns={COLS} rows={payouts} />
        )
      )}
    </div>
  );
}

// ════════════════════════════════
// ANALYTICS
// ════════════════════════════════
export function AnalyticsPage() {
  const [report, setReport] = useState(null);
  const [loading,setLoading]= useState(true);

  useEffect(() => {
    analyticsAPI.getReport()
      .then(r=>setReport(r.data.data))
      .catch(()=>toast.error('Αδυναμία φόρτωσης'))
      .finally(()=>setLoading(false));
  }, []);

  if (loading) return <div className={styles.page}><div className={styles.loading}>Φόρτωση...</div></div>;

  const r = report || {};

  return (
    <div className={styles.page}>
      <PageHeader title="Αναλυτικά & Αναφορές" subtitle="Επίδοση πλατφόρμας"
        action={<ActionBtn onClick={()=>toast('Εξαγωγή PDF...')}>⬇ Εξαγωγή</ActionBtn>} />

      <div className={styles.analyticsGrid}>
        <Card title="Διαβουλεύσεις ανά Ιατρό">
          <BarChart data={(r.consultationsByDoctor||[]).map(d=>({ label:`Δρ. ${d.lastName}`, value:d.count }))} />
        </Card>
        <Card title="Ποσοστά Ακυρώσεων">
          <BarChart data={[
            {label:'Ασθενής (νωρίς)', value:r.patientCancelEarly||0, color:'var(--warning)'},
            {label:'Ασθενής (αργά)',  value:r.patientCancelLate||0,  color:'var(--danger)'},
            {label:'Ιατρός ακύρωσε', value:r.doctorCancel||0,       color:'var(--navy)'},
            {label:'No-shows',        value:r.noShows||0,            color:'var(--muted)'},
          ]} maxValue={10} />
        </Card>
        <Card title="Νέες Εγγραφές Ασθενών">
          <BarChart data={(r.registrations||[]).map(m=>({label:m.monthName, value:m.count}))} />
        </Card>
      </div>

      <div style={{marginTop:20}}>
        <Card title="Αξιοποίηση Ιατρών">
          <DataTable
            columns={[
              {key:'doctor', label:'Ιατρός',      width:'2fr', render:(r,i)=><AvatarCell initials={`${r.firstName?.[0]}${r.lastName?.[0]}`} name={`Δρ. ${r.firstName} ${r.lastName}`} color={ac(i)} />},
              {key:'slots',  label:'Σύνολο Slots', width:'1fr', render:r=>r.totalSlots},
              {key:'booked', label:'Κλεισμένα',   width:'1fr', render:r=>r.bookedSlots},
              {key:'avail',  label:'Ελεύθερα',    width:'1fr', render:r=>r.availableSlots},
              {key:'rate',   label:'Αξιοποίηση',  width:'1fr', render:r=><span className={`pill ${r.utilizationRate>70?'pill-green':'pill-orange'}`}>{r.utilizationRate}%</span>},
            ]}
            rows={r.utilization||[]}
          />
        </Card>
      </div>
    </div>
  );
}

// ════════════════════════════════
// SERVICES
// ════════════════════════════════
export function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showAdd, setShowAdd]   = useState(false);
  const [form, setForm]         = useState({ nameEn:'', nameEl:'', isActive:true });

  // @service-editor-v1 (28 Sep 2026)
  // serviceAPI.update and serviceAPI.reorder have been in api.js since this page
  // was designed, pointing at routes that did not exist until 28 Sep (M-15b).
  // With them wired, changing a service stops being a psql UPDATE typed by hand -
  // which is what M-20 was, and what M-37 will be when the weight-management
  // clinic is switched on.
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);

  const openEdit = (r) => {
    setEditing(r);
    setEditForm({
      nameEn: r.nameEn || '',
      nameEl: r.nameEl || '',
      icon: r.icon || '',
      isActive: !!r.isActive,
    });
  };

  const apiError = (e, fallback) =>
    (e && e.response && e.response.data && e.response.data.error) || fallback;

  const handleSave = async () => {
    if (!editing || !editForm) return;
    if (!editForm.nameEn.trim() || !editForm.nameEl.trim()) {
      toast.error('Συμπληρώστε και τα δύο ονόματα.');
      return;
    }
    setSaving(true);
    try {
      await serviceAPI.update(editing.id, editForm);
      toast.success('Η υπηρεσία ενημερώθηκε.');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e, 'Η αποθήκευση απέτυχε.'));
    } finally {
      setSaving(false);
    }
  };

  // Up and down rather than typing a number on every row: moving one service
  // renumbers the others, and the reorder endpoint takes the whole list, so the
  // ordering can never end up with gaps or two services sharing a position.
  const move = async (i, delta) => {
    const j = i + delta;
    if (i < 0 || j < 0 || j >= services.length) return;
    const ids = services.map((s) => s.id);
    const tmp = ids[i];
    ids[i] = ids[j];
    ids[j] = tmp;
    setReordering(true);
    try {
      await serviceAPI.reorder(ids);
      load();
    } catch (e) {
      toast.error(apiError(e, 'Η αλλαγή σειράς απέτυχε.'));
    } finally {
      setReordering(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try { const {data}=await serviceAPI.getAll(); setServices(data.data); }
    catch { toast.error('Αδυναμία φόρτωσης'); }
    finally { setLoading(false); }
  };

  useEffect(()=>{load();},[]);

  const handleAdd = async () => {
    try { await serviceAPI.create(form); toast.success('✅ Υπηρεσία προστέθηκε'); setShowAdd(false); load(); }
    catch { toast.error('Σφάλμα'); }
  };

  const COLS = [
    { key:'en',     label:'Υπηρεσία (Αγγλικά)', width:'2fr',   render:r=><strong>{r.nameEn}</strong> },
    { key:'el',     label:'Υπηρεσία (Ελληνικά)', width:'2fr',  render:r=>r.nameEl },
    { key:'order',  label:'Σειρά',               width:'0.5fr', render:r=>r.displayOrder },
    { key:'status', label:'Κατάσταση',           width:'0.8fr', render:r=><span className={`pill ${r.isActive?'pill-green':'pill-red'}`}>{r.isActive?'Ενεργή':'Ανενεργή'}</span> },
    { key: 'actions', label: '', width: '150px', render: (r) => {
      const i = services.findIndex((s) => s.id === r.id);
      return (
        <div style={{ display: 'flex', gap: 6 }}>
          <ActionBtn disabled={i <= 0 || reordering} onClick={() => move(i, -1)}>↑</ActionBtn>
          <ActionBtn disabled={i < 0 || i >= services.length - 1 || reordering} onClick={() => move(i, 1)}>↓</ActionBtn>
          <ActionBtn onClick={() => openEdit(r)}>Επεξεργ.</ActionBtn>
        </div>
      );
    } },
  ];

  return (
    <div className={styles.page}>
      <PageHeader title="Τύποι Υπηρεσιών" subtitle="Διαχείριση υπηρεσιών πλατφόρμας"
        action={<ActionBtn variant="primary" onClick={()=>setShowAdd(true)}>+ Προσθήκη Υπηρεσίας</ActionBtn>} />
      {loading ? <div className={styles.loading}>Φόρτωση...</div> : <DataTable columns={COLS} rows={services} />}

      <Modal open={showAdd} onClose={()=>setShowAdd(false)} title="Προσθήκη Νέας Υπηρεσίας" size="sm">
        <div style={{padding:'20px 24px'}}>
          <FormField label="Όνομα (Αγγλικά)" required><Input placeholder="e.g. Digital GP" value={form.nameEn} onChange={e=>setForm({...form,nameEn:e.target.value})} /></FormField>
          <FormField label="Όνομα (Ελληνικά)" required><Input placeholder="π.χ. Ψηφιακός Γενικός Ιατρός" value={form.nameEl} onChange={e=>setForm({...form,nameEl:e.target.value})} /></FormField>
        </div>
        <div className={styles.modalFooter}>
          <ActionBtn variant="default" onClick={()=>setShowAdd(false)}>Ακύρωση</ActionBtn>
          <ActionBtn variant="primary" onClick={handleAdd} disabled={!form.nameEn||!form.nameEl}>Προσθήκη</ActionBtn>
        </div>
      </Modal>

      {/* @service-editor-v1 */}
      <Modal open={!!editing} onClose={() => setEditing(null)} title="Επεξεργασία υπηρεσίας" size="sm">
        {editForm && (
          <div style={{ padding: '20px 24px' }}>
            <FormField label="Όνομα (EN)" required>
              <Input value={editForm.nameEn} onChange={(e) => setEditForm({ ...editForm, nameEn: e.target.value })} />
            </FormField>
            <FormField label="Όνομα (EL)" required>
              <Input value={editForm.nameEl} onChange={(e) => setEditForm({ ...editForm, nameEl: e.target.value })} />
            </FormField>
            <FormField label="Εικονίδιο">
              <Input value={editForm.icon} onChange={(e) => setEditForm({ ...editForm, icon: e.target.value })} />
            </FormField>
            <FormField label="Κατάσταση">
              <Select value={editForm.isActive ? 'true' : 'false'} onChange={(e) => setEditForm({ ...editForm, isActive: e.target.value === 'true' })}>
                <option value="true">Ενεργή</option>
                <option value="false">Ανενεργή</option>
              </Select>
            </FormField>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <ActionBtn onClick={() => setEditing(null)}>Άκυρο</ActionBtn>
              <ActionBtn variant="primary" disabled={saving} onClick={handleSave}>Αποθήκευση</ActionBtn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
